import React, { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import useSystemState from '../stores/SystemState';
import { Downgraded, useHookstate } from "@hookstate/core";
import { Box, Chip, Button } from '@mui/material';
import useUserState from '~/features/User/stores/UserState';
import { useLang } from '~/hooks/useLang';
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import useDispatchState from '~/features/Dispatch/stores/DispatchState';
import { authFetch } from '~/services/fetch';
import notify from '~/utils/notify';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ConfirmDialog from '~/components/ConfirmDialog';
import useDispatchVehicleState from '~/features/Dispatch/stores/DispatchVehicleState';
import { associateDeviceInDispatch } from '~/features/Device/TabListDevice/services';
import type { IListDevice } from '~/features/Device/TabListDevice/interfaces/IListDevice';
import type { IListDispatch } from '~/features/Dispatch/components/TabListDispatch/interfaces/IListDispatch';


type WindowSupportProps = {
  readonly readOnly?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export default function WindowSupport() {
  const { verifyPermission } = useUserState();
  const { dispatch } = useDispatchState()
  const { renderComponents } = useSystemState().system()
  const { getStatusColors, getDeviceStatusColors } = useSystemState()
  const { syncVehicleListInDispatch } = useDispatchVehicleState();
  const ComponentTop = renderComponents.attach(Downgraded).get().top;
  const ComponentBottom = renderComponents.attach(Downgraded).get().bottom;
  // const ComponentThree = renderComponents.attach(Downgraded).get().three;
  const { translate } = useLang();

  const [activeDeviceDrag, setActiveDeviceDrag] = useState<any | null>(null);
  const [activeDispatchDrag, setActiveDispatchDrag] = useState<any | null>(null);
  const openConfirmDialog = useHookstate(false);
  const finalDispatch = useHookstate<IListDispatch | null>(null);
  const finalDevice = useHookstate<IListDevice | null>(null);
  const associtationDirection = useHookstate<'device-to-dispatch' | 'dispatch-to-device'>('device-to-dispatch');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    })
  );

  useEffect(() => {
    document.title = translate('Window Support - Dispatch System')
  }, [])

  const handleAssociate = () => {
    associateDeviceInDispatch({ deviceId: finalDevice.get()?.id, dispatchId: finalDispatch.get()?.id })
      .then(({ status }) => {
        if (status === 200) {
          notify({
            message: translate('Added successfully'),
            type: 'success',
          });
        }
      }).finally(() => {
        dispatch().id.get() === finalDispatch.get()?.id
          ? syncVehicleListInDispatch(Number(finalDispatch.get()?.id))
          : null
      });
  }

  const handleDragStart = (event) => {
    const { active } = event;
    const activeId = String(active.id);

    if (activeId.startsWith('device-')) {
      setActiveDeviceDrag(active.data.current?.device);
      setActiveDispatchDrag(null);
    } else if (activeId.startsWith('dispatch-')) {
      setActiveDispatchDrag(active.data.current?.dispatch);
      setActiveDeviceDrag(null);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveDeviceDrag(null);
      setActiveDispatchDrag(null);
      return;
    }

    const hasActiveDevice = Boolean(active.data.current?.device);
    const hasActiveDispatch = Boolean(active.data.current?.dispatch);
    const hasOverDevice = Boolean(over.data.current?.device);
    const hasOverDispatch = Boolean(over.data.current?.dispatch);
    hasActiveDevice ? associtationDirection.set('device-to-dispatch') : associtationDirection.set('dispatch-to-device');
    const isSameArea = (hasActiveDevice && hasOverDevice) || (hasActiveDispatch && hasOverDispatch);

    if (isSameArea) {
      setActiveDeviceDrag(null);
      setActiveDispatchDrag(null);
      return;
    }

    finalDispatch.set(over.data.current?.dispatch || active.data.current?.dispatch);
    finalDevice.set(over.data.current?.device || active.data.current?.device);

    const sameDispatchGroup = finalDevice.get()?.dispatchGroup === finalDispatch.get()?.dispatch_group;

    finalDispatch.get() && finalDevice.get() && sameDispatchGroup
      ? openConfirmDialog.set(true)
      : notify({
        message: translate('Device and dispatch must be from the same dispatch group'),
        type: 'error',
      })

    setActiveDeviceDrag(null);
    setActiveDispatchDrag(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Layout
        marginLayout={false}
        sidebar={false}
        topbar={false}
      >
        <Box sx={{ paddingBottom: 2 }}>
          <ComponentTop />
        </Box>

        <Box sx={{ paddingBottom: 2 }}>
          <ComponentBottom />
        </Box>

        <DragOverlay dropAnimation={null}>
          {activeDeviceDrag ? (
            <Chip
              icon={<DragIndicatorIcon
                sx={{
                  color: 'primary.main',
                  fontSize: '18px',
                }}
              />}
              sx={{
                backgroundColor: getDeviceStatusColors(activeDeviceDrag.statusId).main,
                color: getDeviceStatusColors(activeDeviceDrag.statusId).contrastText,
                '&:hover': {
                  backgroundColor: getDeviceStatusColors(activeDeviceDrag.statusId).dark,
                  color: getDeviceStatusColors(activeDeviceDrag.statusId).contrastText,
                },
              }}
              label={activeDeviceDrag.identification}
            />
          ) : activeDispatchDrag ? (
            <Button
              variant="contained"
              sx={{
                backgroundColor: getStatusColors(activeDispatchDrag.statusId).main,
                color: getStatusColors(activeDispatchDrag.statusId).contrastText,
                '&:hover': {
                  backgroundColor: getStatusColors(activeDispatchDrag.statusId).dark,
                  color: getStatusColors(activeDispatchDrag.statusId).contrastText,
                },
                cursor: 'grabbing',
              }}
            >
              {activeDispatchDrag.code}
            </Button>
          ) : null}
        </DragOverlay>
        <ConfirmDialog
          open={openConfirmDialog}
          title={translate('Confirm association')}
          content={associtationDirection.get() === 'device-to-dispatch' ?
            `${translate('Associate device to dispatch', { deviceName: finalDevice.get()?.identification, codeDispatch: finalDispatch.get()?.code })}?` :
            `${translate('Associate dispatch to device', { deviceName: finalDevice.get()?.identification, codeDispatch: finalDispatch.get()?.code })}?`}
          handleClose={() => openConfirmDialog.set(false)}
          handleAgree={() => {
            handleAssociate();
            openConfirmDialog.set(false);
          }}
          closeLabel="Cancel"
          AgreeLabel="Confirm"
        />
      </Layout>
    </DndContext>
  );
}
